import React, { useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { DOTTUYENSINH_API } from '@app/utils/apiUrl';
import { useQuery, useMutation } from 'react-query';
import { messageError, messageToast, status200 } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';

const schema = yup.object().shape({
    maDot: yup.string().required(messageError.emptyMessage).max(50, 'Mã đợt tối đa 50 ký tự'),
    tenDot: yup.string().required(messageError.emptyMessage).max(255, 'Tên đợt tối đa 255 ký tự'),
    nam: yup.number().required(messageError.emptyMessage).min(1, 'Năm phải lớn hơn 0')
});

const DotTuyenSinhEdit = () => {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['dotTuyenSinh', itemId], () => Get(DOTTUYENSINH_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const editMutation = useMutation((value) => Put(DOTTUYENSINH_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const model = {
            maDot: values.maDot,
            tenDot: values.tenDot,
            nam: Number(values.nam),
            tuNgay: values.tuNgay || null,
            denNgay: values.denNgay || null,
            moTa: values.moTa || null,
            trangThaiDotTuyenSinh: Number(values.trangThaiDotTuyenSinh)
        };

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    history.push(URI_INTERNAL.DOTTUYENSINH_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    if (itemQuery.isLoading) return <div>Đang tải....</div>;
    if (itemQuery.isError || !itemQuery.data) return <div>Có lỗi xảy ra hoặc không tìm thấy đợt tuyển sinh</div>;

    const currentData = itemQuery.data;

    return (
        <Card>
            <Card.Header as="h5">Sửa đợt tuyển sinh</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        maDot: currentData.maDot || currentData.MaDot || '',
                        tenDot: currentData.tenDot || currentData.TenDot || '',
                        nam: currentData.nam || currentData.Nam || '',
                        tuNgay: currentData.tuNgay ? currentData.tuNgay.split('T')[0] : currentData.TuNgay || '',
                        denNgay: currentData.denNgay ? currentData.denNgay.split('T')[0] : currentData.DenNgay || '',
                        moTa: currentData.moTa || currentData.MoTa || '',
                        trangThaiDotTuyenSinh: (currentData.trangThaiDotTuyenSinh ?? currentData.TrangThaiDotTuyenSinh ?? 0)
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Mã đợt</Form.Label>
                                <Col sm={10}>
                                    <Form.Control name="maDot" value={values.maDot} onChange={handleChange} isInvalid={!!errors.maDot} />
                                    <Form.Control.Feedback type="invalid">{errors.maDot}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Tên đợt</Form.Label>
                                <Col sm={10}>
                                    <Form.Control name="tenDot" value={values.tenDot} onChange={handleChange} isInvalid={!!errors.tenDot} />
                                    <Form.Control.Feedback type="invalid">{errors.tenDot}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Năm</Form.Label>
                                <Col sm={10}>
                                    <Form.Control type="number" name="nam" value={values.nam} onChange={handleChange} isInvalid={!!errors.nam} />
                                    <Form.Control.Feedback type="invalid">{errors.nam}</Form.Control.Feedback>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Từ ngày</Form.Label>
                                <Col sm={10}>
                                    <Form.Control type="date" name="tuNgay" value={values.tuNgay} onChange={handleChange} />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Đến ngày</Form.Label>
                                <Col sm={10}>
                                    <Form.Control type="date" name="denNgay" value={values.denNgay} onChange={handleChange} />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Mô tả</Form.Label>
                                <Col sm={10}>
                                    <Form.Control as="textarea" name="moTa" rows={3} value={values.moTa} onChange={handleChange} />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Form.Label column sm={2}>Trạng thái</Form.Label>
                                <Col sm={10}>
                                    <Form.Control as="select" name="trangThaiDotTuyenSinh" value={values.trangThaiDotTuyenSinh} onChange={handleChange}>
                                        <option value={0}>Nhập</option>
                                        <option value={1}>Đang mở đăng ký</option>
                                        <option value={2}>Đã đóng đăng ký</option>
                                        <option value={3}>Đang xét duyệt</option>
                                        <option value={4}>Đã công bố kết quả</option>
                                        <option value={5}>Tạm dừng</option>
                                        <option value={6}>Hủy</option>
                                    </Form.Control>
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">Cập nhật</Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.DOTTUYENSINH_PATH)} theme="secondary">Hủy</Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default DotTuyenSinhEdit;
